import {
  WebDraftPayload,
  buildWebDraftPayload,
} from '@/lib/draft/characterDraft'
import { CreateCharForm } from '@/store/create'

const CACHE_PREFIX = 'tipsy:create-character-cache:v1'
const DEFAULT_SOURCE = 'character_image'
const CACHE_VERSION = 1

export type LocalCharacterDraftCacheKeyOptions = {
  userId?: string | null
  isUpdateChar: boolean
  source?: string | null
  characterId?: string | null
}

export type LocalCharacterDraftCacheRecord = {
  version: 1
  saved_at: number
  user_id: string
  is_update: boolean
  source: string
  character_id: string
  title: string
  description: string
  payload_json: WebDraftPayload
}

const hasWindowStorage = () =>
  typeof window !== 'undefined' && typeof window.localStorage !== 'undefined'

const encodeKeyPart = (value: string) => encodeURIComponent(value)

export const getLocalCharacterDraftSource = (source?: string | null) =>
  source || DEFAULT_SOURCE

export const getLocalCharacterDraftCacheKey = ({
  userId,
  isUpdateChar,
  source,
  characterId,
}: LocalCharacterDraftCacheKeyOptions) => {
  if (!userId) return null

  const normalizedSource = getLocalCharacterDraftSource(source)
  const normalizedCharacterId = isUpdateChar ? characterId || 'unknown' : 'new'

  return [
    CACHE_PREFIX,
    encodeKeyPart(userId),
    isUpdateChar ? 'update' : 'create',
    encodeKeyPart(normalizedSource),
    encodeKeyPart(normalizedCharacterId),
  ].join(':')
}

const hasStringValue = (value: unknown) =>
  typeof value === 'string' && value.trim().length > 0

const hasArrayValue = (value: unknown) =>
  Array.isArray(value) && value.length > 0

export const hasMeaningfulCharacterDraftPayload = (
  payload: Record<string, unknown>
) =>
  [
    payload.charNickname,
    payload.charShortDescribe,
    payload.charLongDescribe,
    payload.charGreeting,
    payload.charScenario,
    payload.name,
    payload.tagline,
    payload.personality,
    payload.greeting,
    payload.prompt,
    payload.imagePrompt,
    payload.exampleDialogues,
    payload.replyRequirement,
    payload.optimizationCode,
    payload.outputFormatConfig,
    payload.charImageUrl,
    payload.charImagePath,
    payload.charPcImageUrl,
    payload.charPcImagePath,
    payload.charFaceImagePreviewUrl,
    payload.charFaceImagePath,
    payload.uploadUploadImageUrl,
    payload.uploadUpscaleImageUrl,
    payload.uploadVaryImageUrl,
    payload.uploadGifUrl,
    payload.uploadGifKey,
    payload.uploadImage,
    payload.uploadImageKey,
    payload.avatarImage,
    payload.avatarImageKey,
    payload.image,
    payload.imageKey,
    payload.face,
    payload.faceKey,
    payload.gifImage,
    payload.gifImageKey,
    payload.greetingVideoUrl,
    payload.greetingVideoKey,
    payload.plot_file_key,
    payload.plotFileKey,
    payload.charBaseTone,
    payload.voiceId,
    payload.test_chat_tcid,
  ].some(hasStringValue) ||
  [
    payload.charTagIDs,
    payload.role_character_ids,
    payload.role_character_list,
    payload.character_ids,
    payload.id_list,
    payload.tags,
    payload.world_books,
    payload.aiGeneratedImages,
    payload.test_chat_history,
    payload.test_chat_pending,
  ].some(hasArrayValue) ||
  payload.charGender != null ||
  payload.gender != null ||
  payload.nsfwCheck === true ||
  payload.rating === true ||
  payload.advancedSetCheck === true ||
  payload.contentType === 2 ||
  payload.character_type === 2

export const hasMeaningfulCharacterDraftForm = (form: CreateCharForm) =>
  hasMeaningfulCharacterDraftPayload(buildWebDraftPayload(form))

export const getLocalCharacterDraftCachePayloadSnapshot = (
  form: CreateCharForm
) => {
  const payload = buildWebDraftPayload(form)
  if (!hasMeaningfulCharacterDraftPayload(payload)) return null

  try {
    return JSON.stringify(payload)
  } catch {
    return null
  }
}

const readRawCacheRecord = (
  value: string | null
): LocalCharacterDraftCacheRecord | null => {
  if (!value) return null

  try {
    const parsed = JSON.parse(value) as LocalCharacterDraftCacheRecord
    if (parsed?.version !== CACHE_VERSION) return null
    if (!parsed.payload_json || typeof parsed.payload_json !== 'object') {
      return null
    }
    if (!hasMeaningfulCharacterDraftPayload(parsed.payload_json)) return null
    return parsed
  } catch {
    return null
  }
}

export const readLocalCharacterDraftCache = (cacheKey: string | null) => {
  if (!cacheKey || !hasWindowStorage()) return null
  try {
    return readRawCacheRecord(window.localStorage.getItem(cacheKey))
  } catch {
    return null
  }
}

export const saveLocalCharacterDraftCache = (
  cacheKey: string | null,
  form: CreateCharForm,
  options: LocalCharacterDraftCacheKeyOptions
) => {
  if (!cacheKey || !hasWindowStorage()) return

  const payload = buildWebDraftPayload(form)
  if (!hasMeaningfulCharacterDraftPayload(payload)) {
    return
  }

  const record: LocalCharacterDraftCacheRecord = {
    version: CACHE_VERSION,
    saved_at: Date.now(),
    user_id: options.userId || '',
    is_update: options.isUpdateChar,
    source: getLocalCharacterDraftSource(options.source),
    character_id: options.isUpdateChar
      ? options.characterId || 'unknown'
      : 'new',
    title: (form.charNickname || '').trim(),
    description: (form.charShortDescribe || '').trim(),
    payload_json: payload,
  }

  try {
    window.localStorage.setItem(cacheKey, JSON.stringify(record))
  } catch {
    // local cache is best-effort only
  }
}

export const clearLocalCharacterDraftCache = (cacheKey: string | null) => {
  if (!cacheKey || !hasWindowStorage()) return
  try {
    window.localStorage.removeItem(cacheKey)
  } catch {
    // local cache is best-effort only
  }
}

export const clearLocalCharacterDraftCacheByOptions = (
  options: LocalCharacterDraftCacheKeyOptions
) => {
  clearLocalCharacterDraftCache(getLocalCharacterDraftCacheKey(options))
}

export const clearLocalCharacterDraftCachesForUser = (
  userId?: string | null
) => {
  if (!userId || !hasWindowStorage()) return

  const userPrefix = `${CACHE_PREFIX}:${encodeKeyPart(userId)}:`
  const keysToRemove: string[] = []

  try {
    for (let index = 0; index < window.localStorage.length; index += 1) {
      const key = window.localStorage.key(index)
      if (key?.startsWith(userPrefix)) {
        keysToRemove.push(key)
      }
    }

    keysToRemove.forEach((key) => window.localStorage.removeItem(key))
  } catch {
    // local cache is best-effort only
  }
}
